import ExcelJS from "exceljs";
import { ChevronDown, Download, ExternalLink, Pencil, Plus, Trash } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { montageApi, type WorkEntry, type WorkInput } from "../../api";
import { Button } from "../../components/ui/button";
import { FilterChip, Segmented } from "../../components/ui/primitives";
import { notify } from "../../lib/notify";
import { useUrlOptional, useUrlState } from "../../lib/urlState";
import { confirmDialog } from "../../../../shared/ui/ConfirmHost";
import { cn } from "../../lib/utils";
import { monthLabel, monthRange, recentMonths } from "../videos/month";

/**
 * «Отработано» (25.10.2026, Жулдыз): учёт того, что сделала Саяна.
 *
 * Задачи ей приходят в WhatsApp (Абай → Жулдыз → Саяна), и этот канал
 * остаётся. Здесь — только результат: ссылки на вышедшее, было ли задание,
 * при желании — его текст. Взятые ролики B2G («Взяла») попадают сюда сами.
 * Отчёт за период — у неё и у руководителя: сколько материалов, сколько без
 * ТЗ, ссылки и их просмотры. Саяна работает с телефона — форма сверху,
 * в одну колонку.
 */

const PERIODS = [
  { value: "7", label: "7 дней" },
  { value: "30", label: "30 дней" },
  { value: "month", label: "Месяц" },
];

const today = () => new Date(Date.now() + 5 * 3600e3).toISOString().slice(0, 10);
const daysAgo = (days: number) => new Date(Date.now() + 5 * 3600e3 - (days - 1) * 86400e3).toISOString().slice(0, 10);
const num = (n: number | null) => (n == null ? "—" : n.toLocaleString("ru-RU"));
const dayLabel = (date: string) => new Date(`${date}T12:00:00+05:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });

/** Ссылки из вставленного текста: всё, что похоже на адрес, по одной. */
export function linksFrom(text: string): string[] {
  return [...new Set((text.match(/https?:\/\/[^\s,;]+/gi) ?? []).map((l) => l.replace(/[).]+$/, "")))];
}

export interface WorkTotals { entries: number; noBrief: number; links: number; views: number | null }

export function workTotals(entries: WorkEntry[]): WorkTotals {
  const links = entries.flatMap((e) => e.links);
  return {
    entries: entries.length,
    noBrief: entries.filter((e) => !e.withBrief).length,
    links: links.length,
    views: links.some((l) => l.views != null) ? links.reduce((n, l) => n + (l.views ?? 0), 0) : null,
  };
}

/** Заголовок записи: название, иначе первая строка ТЗ, иначе адрес первой ссылки. */
export function entryTitle(e: Pick<WorkEntry, "title" | "brief" | "links">): string {
  const brief = e.brief.split("\n").map((l) => l.trim()).find(Boolean);
  const link = e.links[0]?.url.replace(/^https?:\/\/(www\.)?/, "");
  const raw = e.title || brief || link || "Материал";
  return raw.length > 80 ? `${raw.slice(0, 79)}…` : raw;
}

const entryViews = (e: WorkEntry) => (e.links.some((l) => l.views != null) ? e.links.reduce((n, l) => n + (l.views ?? 0), 0) : null);

function WorkForm({ initial, onSaved, onCancel }: { initial?: WorkEntry; onSaved: () => void; onCancel?: () => void }) {
  const [links, setLinks] = useState(initial?.links.map((l) => l.url).join("\n") ?? "");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [withBrief, setWithBrief] = useState(initial?.withBrief ?? true);
  const [brief, setBrief] = useState(initial?.brief ?? "");
  const [workedOn, setWorkedOn] = useState(initial?.workedOn ?? today());
  const [busy, setBusy] = useState(false);
  const parsed = linksFrom(links);
  const ready = parsed.length > 0 || title.trim().length >= 3;
  const field = "w-full rounded-[16px] border border-input bg-background px-3.5 py-2.5 text-sm outline-none focus:ring-4 focus:ring-ring/20";

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    const body: WorkInput = { title: title.trim(), withBrief, brief: withBrief ? brief.trim() : "", links: parsed, workedOn };
    try {
      if (initial) await montageApi.editWork(initial.id, body);
      else await montageApi.addWork(body);
      notify(initial ? "Запись поправлена" : `Записано · ${parsed.length || "без"} ${parsed.length === 1 ? "ссылка" : "ссылок"}`, { icon: "check" });
      if (!initial) { setLinks(""); setTitle(""); setBrief(""); setWithBrief(true); setWorkedOn(today()); }
      onSaved();
    } catch (e) {
      notify((e as Error).message, { icon: "rework" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3 rounded-[20px] border bg-card p-4" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <span className="text-sm font-semibold">{initial ? "Поправить запись" : "Что сделано"}</span>
      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] text-muted-foreground">Ссылки на вышедшее — можно вставить пачкой</span>
        <textarea value={links} onChange={(e) => setLinks(e.target.value)} rows={3} placeholder="https://www.instagram.com/reel/…" className={cn(field, "resize-none font-mono text-[13px]")} />
        {parsed.length > 0 && <span className="text-xs text-muted-foreground">ссылок: {parsed.length}</span>}
      </label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="О чём материал — необязательно" className={field} />
      <div className="flex flex-wrap items-center gap-2">
        <FilterChip active={withBrief} onClick={() => setWithBrief(true)}>По задаче</FilterChip>
        <FilterChip active={!withBrief} onClick={() => setWithBrief(false)}>Без ТЗ</FilterChip>
        <span className="flex-1" />
        <input type="date" value={workedOn} max={today()} onChange={(e) => setWorkedOn(e.target.value)} className="h-9 rounded-full border bg-background px-3 text-[13px]" aria-label="Дата" />
      </div>
      {withBrief && (
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} placeholder="Что просили — можно вставить из WhatsApp (необязательно)" className={cn(field, "resize-none")} />
      )}
      <div className="flex justify-end gap-2">
        {onCancel && <Button type="button" variant="outline" onClick={onCancel}>Отмена</Button>}
        <Button type="submit" disabled={!ready || busy}>
          {!initial && <Plus />}
          {initial ? "Сохранить" : "Записать"}
        </Button>
      </div>
    </form>
  );
}

function EntryRow({ entry, showWho, onChange }: { entry: WorkEntry; showWho: boolean; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  if (editing) return <WorkForm initial={entry} onSaved={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />;
  const views = entryViews(entry);
  return (
    <div className="flex flex-col gap-2 rounded-[18px] border bg-card px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">{entryTitle(entry)}</span>
            {!entry.withBrief && <span className="rounded-full bg-publish-soft px-2 py-0.5 text-[11px] font-semibold text-publish-fg">без ТЗ</span>}
            {entry.fromTake && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">ролик B2G</span>}
          </div>
          <span className="text-xs text-muted-foreground">
            {dayLabel(entry.workedOn)}{showWho ? ` · ${entry.user.name}` : ""} · {entry.links.length} {entry.links.length === 1 ? "ссылка" : "ссылок"} · {num(views)} просм.
          </span>
        </div>
        {entry.mine && (
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => setEditing(true)} title="Поправить" className="grid size-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="size-4" /></button>
            <button
              type="button"
              title="Удалить"
              onClick={async () => {
                if (!(await confirmDialog({ title: "Удалить запись из «Отработано»?", description: entryTitle(entry), confirmText: "Удалить", destructive: true }))) return;
                try { await montageApi.removeWork(entry.id); onChange(); } catch (e) { notify((e as Error).message, { icon: "rework" }); }
              }}
              className="grid size-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-rework-fg"
            ><Trash className="size-4" /></button>
          </div>
        )}
        {(entry.links.length > 0 || entry.brief) && (
          <button type="button" onClick={() => setOpen(!open)} aria-label="Подробнее" className="grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted">
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-1.5">
          {entry.brief && <p className="whitespace-pre-wrap rounded-[12px] bg-muted/60 px-3 py-2 text-[13px]">{entry.brief}</p>}
          {entry.links.map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground">
              <ExternalLink className="size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{l.url.replace(/^https?:\/\/(www\.)?/, "")}</span>
              <span className="shrink-0 tabular-nums">{num(l.views)}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

async function exportWorkXlsx(label: string, entries: WorkEntry[]): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();
  const totals = workTotals(entries);
  const sheet = wb.addWorksheet("Отработано");
  sheet.addRow([`Отработано · ${label}`]).font = { bold: true, size: 13 };
  sheet.addRow([`Материалов: ${totals.entries} · без ТЗ: ${totals.noBrief} · ссылок: ${totals.links} · просмотров: ${totals.views ?? "—"}`]);
  sheet.addRow([]);
  sheet.addRow(["Дата", "Сотрудник", "Материал", "ТЗ", "Ссылка", "Просмотры"]).font = { bold: true };
  for (const e of entries) {
    const rows = e.links.length ? e.links : [{ url: "", views: null }];
    for (const l of rows) {
      sheet.addRow([dayLabel(e.workedOn), e.user.name, e.title, e.withBrief ? (e.brief || "по задаче") : "без ТЗ", l.url ? { text: l.url, hyperlink: l.url } : "", l.views ?? "—"]);
    }
  }
  sheet.columns.forEach((c, i) => { c.width = [10, 22, 40, 30, 52, 12][i]; });
  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `Отработано ${label}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export function Work() {
  // Период, месяц и человек — в адресе (02.11.2026): «Назад» и ссылка
  // руководителю открывают тот же срез; период ещё и запомнен.
  const [period, setPeriod] = useUrlState("period", "30", { remember: "montage:work:period" });
  const months = useMemo(() => recentMonths(), []);
  const [monthRaw, setMonth] = useUrlState("month", months[0]);
  const month = months.includes(monthRaw) ? monthRaw : months[0];
  const [data, setData] = useState<{ entries: WorkEntry[]; canAdd: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [person, setPerson] = useUrlOptional("person");
  const [noBriefOnly, setNoBriefOnly] = useState(false);
  const [from, to] = period === "month" ? monthRange(month) : [daysAgo(Number(period)), today()];
  const label = period === "month" ? monthLabel(month) : `${dayLabel(from)} — ${dayLabel(to)}`;

  const load = useCallback(() => {
    setError(null);
    montageApi.work(from, to).then(setData).catch((e) => setError((e as Error).message));
  }, [from, to]);
  useEffect(() => { setData(null); load(); }, [load]);

  const all = data?.entries ?? [];
  const people = useMemo(() => {
    const map = new Map<string, { id: string; name: string; entries: WorkEntry[] }>();
    for (const e of all) {
      const p = map.get(e.user.id) ?? { id: e.user.id, name: e.user.name, entries: [] };
      p.entries.push(e);
      map.set(e.user.id, p);
    }
    return [...map.values()].sort((a, b) => b.entries.length - a.entries.length);
  }, [all]);
  const byPerson = person ? all.filter((e) => e.user.id === person) : all;
  const shown = noBriefOnly ? byPerson.filter((e) => !e.withBrief) : byPerson;
  const totals = workTotals(byPerson);
  const others = all.some((e) => !e.mine);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="sr-only">Отработано</h1>
        <Segmented id="work-period" value={period} onChange={setPeriod} options={PERIODS} />
        {period === "month" && (
          <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Месяц" className="h-9 rounded-full border bg-background px-3 text-[13px] font-medium">
            {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        )}
        <span className="flex-1" />
        <button
          type="button"
          disabled={!shown.length}
          onClick={() => void exportWorkXlsx(label, shown).catch((e) => notify((e as Error).message, { icon: "rework" }))}
          className="flex items-center gap-1.5 text-[13px] font-medium underline underline-offset-2 disabled:opacity-50"
        >
          <Download className="size-4" /> Excel
        </button>
      </header>

      <div className="scroll-thin flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-6">
        {data?.canAdd && <WorkForm onSaved={load} />}

        <section className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          {[
            ["Материалов", totals.entries],
            ["Без ТЗ", totals.noBrief],
            ["Ссылок", totals.links],
            ["Просмотров", totals.views],
          ].map(([name, value]) => (
            <div key={name as string} className="flex flex-col gap-0.5 rounded-[18px] border bg-card px-4 py-3">
              <span className="text-xs text-muted-foreground">{name}</span>
              <span className="text-xl font-semibold tabular-nums">{num(value as number | null)}</span>
            </div>
          ))}
        </section>

        <div className="flex flex-wrap items-center gap-2">
          {others && people.length > 0 && (
            <>
              <FilterChip active={!person} onClick={() => setPerson(null)}>Все</FilterChip>
              {people.map((p) => (
                <FilterChip key={p.id} active={person === p.id} onClick={() => setPerson(p.id)} count={p.entries.length}>{p.name}</FilterChip>
              ))}
              <span className="mx-1 h-5 w-px bg-border" />
            </>
          )}
          <FilterChip active={noBriefOnly} onClick={() => setNoBriefOnly(!noBriefOnly)} count={totals.noBrief}>Только без ТЗ</FilterChip>
          <span className="text-[13px] text-muted-foreground">{label}</span>
        </div>

        {error && <p className="text-sm text-rework-fg">Не удалось загрузить: {error}</p>}
        {!data && !error ? (
          <p className="text-sm text-muted-foreground">Загружаем…</p>
        ) : shown.length === 0 ? (
          <p className="rounded-[20px] border border-dashed py-10 text-center text-sm text-muted-foreground">
            {data?.canAdd ? "За этот период записей нет — добавьте ссылки выше." : "За этот период записей нет."}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {shown.map((e) => <EntryRow key={e.id} entry={e} showWho={others} onChange={load} />)}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Взятые ролики B2G попадают сюда сами. Просмотры — последний снимок поста; прочерк — пост ещё не попал в статистику.
        </p>
      </div>
    </div>
  );
}
